import { FC, useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import { Coordinate } from 'ol/coordinate';
import { API_BASE_URL } from '@/constants';
import axios from 'axios';
import { NbButton } from '@/components/Neobrut';
import { Input } from '../ui/input';
import { Loader2Icon, SearchIcon } from 'lucide-react';
import { useAuthToken } from '@/hooks/useAuthToken';

interface CustomMapProps {
  setResponse: (data: LocationIQReverseGeocodingResponse) => void;
}

const CustomMap: FC<CustomMapProps> = ({ setResponse }) => {
  const { token } = useAuthToken();

  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [markerLayer, setMarkerLayer] = useState<VectorLayer<VectorSource> | null>(null);
  const [coordinates, setCoordinates] = useState<{ lon: number; lat: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<LocationIQForwardGeocodingResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!mapRef.current) return;

    const newMap = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat([-60.6505, -32.9468]), // Rosario
        zoom: 12,
      }),
    });

    setMap(newMap);

    return () => newMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    if (!map || !coordinates) return;

    if (markerLayer) {
      map.removeLayer(markerLayer);
    }

    const newMarkerLayer = new VectorLayer({
      source: new VectorSource({
        features: [
          new Feature({
            geometry: new Point(fromLonLat([coordinates.lon, coordinates.lat])),
          }),
        ],
      }),
      style: new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: 'https://cdn-icons-png.flaticon.com/32/684/684908.png',
        }),
      }),
    });

    map.addLayer(newMarkerLayer);
    setMarkerLayer(newMarkerLayer);
  }, [map, coordinates]); // eslint-disable-line

  useEffect(() => {
    if (!map || !token) return;

    const handleClick = async (event: Record<'coordinate', Coordinate>) => {
      const clickedCoord = event.coordinate;
      const [lon, lat] = toLonLat(clickedCoord);
      setCoordinates({ lon, lat });

      try {
        const response = await axios.get(
          `${API_BASE_URL}/geocoding/reverse/?lat=${lat}&lon=${lon}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data: LocationIQReverseGeocodingResponse = response.data;

        if (data) {
          setResponse(data);
        } else {
          console.error('No se encontró la dirección');
        }
      } catch (error) {
        console.error('Error obteniendo la dirección:', error);
      }
    };

    map.on('click', handleClick);
    return () => map.un('click', handleClick);
  }, [map]); // eslint-disable-line

  const handleSearch = async () => {
    if (!searchQuery || !token) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/geocoding/forward/?q=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data: LocationIQForwardGeocodingResponse[] = response.data;

      if (data && data.length > 0) {
        setSearchResults(data);
      } else {
        console.error('No se encontraron resultados');
      }
    } catch (error) {
      console.error('Error buscando la dirección:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: LocationIQForwardGeocodingResponse) => {
    const { lon, lat } = result;
    setCoordinates({ lon: parseFloat(lon), lat: parseFloat(lat) });
    setResponse(result);
    setSearchResults([]);

    if (map) {
      const view = map.getView();
      view.setCenter(fromLonLat([parseFloat(lon), parseFloat(lat)]));
      view.setZoom(14);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 pb-3">
      <div className="flex-shrink-0">
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar dirección…"
          className="w-full border-2 border-[var(--nb-ink)] p-2 text-[14px] font-medium focus-visible:ring-0"
          style={{ borderRadius: 'var(--nb-radius)', background: 'var(--nb-paper)' }}
        />
        <NbButton
          fill="ink"
          onClick={handleSearch}
          disabled={loading}
          className="mt-2 w-full py-2.5"
        >
          {loading ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SearchIcon size={14} />}
          Buscar
        </NbButton>

        <ul
          className={`nb-scroll-x mt-2 overflow-y-auto transition-all duration-200 ease-in-out ${
            searchResults.length > 0 ? 'max-h-40 opacity-100' : 'max-h-0 overflow-hidden opacity-0'
          }`}
          style={
            searchResults.length > 0
              ? {
                  border: '2px solid var(--nb-ink)',
                  background: 'var(--nb-paper)',
                  borderRadius: 'var(--nb-radius)',
                }
              : {}
          }
        >
          {searchResults.map((result, index) => (
            <li
              key={index}
              onClick={() => handleResultClick(result)}
              className="cursor-pointer border-b-2 border-[var(--nb-ink)] p-2 text-[13px] font-medium last:border-b-0 hover:bg-[var(--nb-bg)]"
            >
              {result.display_name}
            </li>
          ))}
        </ul>
      </div>

      <div
        className="flex-grow overflow-hidden"
        style={{
          border: '2px solid var(--nb-ink)',
          borderRadius: 'var(--nb-radius)',
          background: 'var(--nb-paper)',
        }}
      >
        <div
          ref={mapRef}
          className="h-full w-full"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export { CustomMap };
